import { useCallback, useEffect, useMemo, useRef, useState } from "react";

import { zodResolver } from "@hookform/resolvers/zod";
import { Button, LogSentryException, TextField } from "@shapr3d/frontend-lib";
import classNames from "classnames";
import { Controller, useForm } from "react-hook-form";
import { z } from "zod";

import useTranslation from "../../hooks/useTranslation";
import { AuthError } from "../../types";
import { FormWrapper } from "../FormWrapper/FormWrapper";
import Heading from "../Heading/Heading";
import ReCAPTCHA, { type ReCAPTCHAAPI } from "../ReCAPTCHA/ReCAPTCHA";
import Disclaimer from "../SignUp/Disclaimer/Disclaimer";
import {
  passwordSchema,
  ValidatedPasswordInput,
} from "../ValidatedPasswordInput/ValidatedPasswordInput";

import style from "./ProjectViewLinkInvitationSignUp.module.css";

interface SignUpProps {
  submit: (email: string, password: string, name: string, recaptcha?: string) => void;
  inProgress: boolean;
  signupError?: AuthError | null;
  disableCaptcha?: boolean;
  emailHint: string;
}

const createSchema = (
  t: ReturnType<typeof useTranslation>["t"],
  m: ReturnType<typeof useTranslation>["m"]
) =>
  z
    .object({
      email: z.string(),
      name: z
        .string()
        .trim()
        .min(1, { message: t(m.ErrorMessage.EmptyName) }),
      password: passwordSchema,
      passwordAgain: z.string(),
      areTermsAccepted: z.boolean().default(false),
    })
    .refine((data) => data.password !== "", {
      path: ["passwordAgain"],
      message: t(m.ErrorMessage.InvalidPassword),
    })
    .refine((data) => data.password === data.passwordAgain, {
      path: ["passwordAgain"],
      message: t(m.ErrorMessage.PasswordsDontMatch),
    })
    .refine((data) => data.areTermsAccepted, {
      path: ["areTermsAccepted"],
      message: t(m.ErrorMessage.TermsAndPrivacyNotAccepted),
    });

type SignUpFormData = z.infer<ReturnType<typeof createSchema>>;

export const ProjectViewLinkInvitationSignUp = ({
  submit,
  inProgress,
  signupError,
  disableCaptcha,
  emailHint,
}: SignUpProps) => {
  const { t, m } = useTranslation();
  const [isCaptchaEnabled, setIsCaptchaEnabled] = useState(false);
  const [isRecaptchaLoaded, setIsRecaptchaLoaded] = useState(false);
  const recaptchaRef = useRef<ReCAPTCHAAPI>(null);
  const schema = useMemo(() => createSchema(t, m), [t, m]);

  const {
    handleSubmit,
    control,
    register,
    setError,
    formState: { errors },
  } = useForm({
    resolver: zodResolver(schema),
    defaultValues: {
      email: emailHint,
      password: "",
      passwordAgain: "",
      name: "",
      areTermsAccepted: false,
    },
  });

  useEffect(() => {
    if (signupError === AuthError.InvalidEmail) {
      setError("root", { message: t(m.ErrorMessage.InvalidEmail) });
    } else if (signupError === AuthError.EmailAlreadyUsed) {
      setError("root", { message: t(m.ErrorMessage.EmailAlreadyUsed) });
    } else if (signupError === AuthError.Unknown) {
      setError("root", { message: t(m.ErrorMessage.Unknown) });
    } else if (signupError === AuthError.MissingCaptcha) {
      setIsCaptchaEnabled(true);
    }
  }, [signupError, setError, t, m]);

  const onSubmit = useCallback(
    async (data: SignUpFormData) => {
      const { email, password, name } = data;

      if (disableCaptcha || !isCaptchaEnabled) {
        submit(email, password, name);
        return;
      }

      if (recaptchaRef.current === null) {
        setError("root", { type: "manual", message: t(m.ErrorMessage.CaptchaError) });
        return;
      }

      try {
        const recaptchaValue = await recaptchaRef.current.execute();
        if (recaptchaValue === "cancel") {
          return;
        }
        if (!recaptchaValue) {
          setError("root", { type: "manual", message: t(m.ErrorMessage.CaptchaError) });
          return;
        }
        submit(email, password, name, recaptchaValue);
      } catch (error) {
        setError("root", { type: "manual", message: t(m.ErrorMessage.CaptchaError) });
        LogSentryException({ messagePrefix: "Captcha error", error });
      }
    },
    [disableCaptcha, isCaptchaEnabled, submit, setError, t, m]
  );

  useEffect(() => {
    if (
      isCaptchaEnabled &&
      isRecaptchaLoaded &&
      recaptchaRef.current !== null &&
      signupError === AuthError.MissingCaptcha
    ) {
      handleSubmit(onSubmit)();
    }
  }, [isCaptchaEnabled, recaptchaRef, handleSubmit, isRecaptchaLoaded, signupError, onSubmit]);

  return (
    <FormWrapper>
      <div className={style.modalContainer}>
        <Heading type="h2" aria-label="title">
          {t(m.SignUp.CreateShapr3DAccountToReview, { br: <br /> })}
        </Heading>
        <form className={style.signUpForm} onSubmit={handleSubmit(onSubmit)} noValidate>
          <TextField
            errorMessage={errors.email?.message}
            type="email"
            aria-label="email"
            {...register("email")}
            error={Boolean(errors.email)}
            disabled
          />

          <ValidatedPasswordInput
            type="password"
            placeholder={t(m.SignUp.PasswordPlaceholder)}
            autoComplete="new-password"
            error={Boolean(errors.password)}
            disabled={inProgress}
            {...register("password")}
          />

          <TextField
            errorMessage={errors.passwordAgain?.message}
            type="password"
            aria-label="repeat password"
            placeholder={t(m.SignUp.ReEnterPasswordPlaceholder)}
            {...register("passwordAgain")}
            error={Boolean(errors.password) || Boolean(errors.passwordAgain)}
            disabled={inProgress}
          />

          <TextField
            errorMessage={errors.name?.message}
            aria-label="name"
            placeholder={t(m.SignUp.NamePlaceholder)}
            {...register("name")}
            error={Boolean(errors.name)}
            disabled={inProgress}
            maxLength={200}
          />

          <div>
            <Controller
              name="areTermsAccepted"
              control={control}
              render={({ field }) => (
                <Disclaimer
                  className={classNames(style.disclaimer)}
                  areTermsAccepted={field.value}
                  handleToggleTermsAccepted={() => field.onChange(!field.value)}
                />
              )}
            />
            {errors.areTermsAccepted && (
              <span className={style.disclaimerErrorMessage}>
                {errors.areTermsAccepted.message}
              </span>
            )}
          </div>

          {isCaptchaEnabled && !disableCaptcha && (
            <ReCAPTCHA
              ref={recaptchaRef}
              onLoad={() => {
                setIsRecaptchaLoaded(true);
              }}
            />
          )}

          {errors.root && <span className={style.rootErrorMessage}>{errors.root.message}</span>}

          <Button id="signup-button" type="submit" aria-label="signup" disabled={inProgress}>
            {t(m.SignUp.SignUpAndReviewDesign)}
          </Button>
        </form>
      </div>
    </FormWrapper>
  );
};
